import React from 'react';
import { cn } from '../../lib/utils';
import { LucideIcon } from 'lucide-react';

export const SectionHeader = ({ icon: Icon, title, className }: { icon: LucideIcon, title: string, className?: string }) => (
  <h5 className={cn("font-medium text-ink uppercase text-[11px] tracking-[0.4em] mb-8 flex items-center gap-4", className)}>
    <Icon className="w-5 h-5 text-oxblood" />
    {title}
  </h5>
);

export const Callout = ({ title, content, icon: Icon, action, className }: { title?: React.ReactNode, content: React.ReactNode, icon?: LucideIcon, action?: React.ReactNode, className?: string }) => (
  <div className={cn("pdf-section mb-10 p-8 rounded-lg oi-card", className)}>
    {(title || action) && (
      <div className="flex justify-between items-start mb-8 relative z-10 gap-4">
        {title && (
          <h5 className="font-medium text-oxblood uppercase text-[11px] tracking-[0.4em] flex items-center gap-4 m-0">
            {Icon && <Icon className="w-5 h-5" />}
            {title}
          </h5>
        )}
        {action && (
          <div className="shrink-0">{action}</div>
        )}
      </div>
    )}
    <div className="text-2xl text-ink leading-[1.6] font-medium tracking-tight relative z-10">
      {content}
    </div>
  </div>
);

export const DarkEmphasisCard = ({ title, content, children, icon: Icon, className }: { title?: string, content?: React.ReactNode, children?: React.ReactNode, icon?: LucideIcon, className?: string }) => (
  <div className={cn("pdf-section mb-10 p-8 rounded-lg oi-card", className)}>
    {title && (
      <h5 className="font-medium text-ink-500 uppercase text-[11px] tracking-[0.4em] mb-8 flex items-center gap-4 relative z-10">
        {Icon && <Icon className="w-5 h-5 text-oxblood" />}
        {title}
      </h5>
    )}
    <div className="relative z-10">
      {content ? (
        <div className="text-lg text-ink-600 leading-relaxed">{content}</div>
      ) : children}
    </div>
  </div>
);

export const MetricStrip = ({ metrics }: { metrics: { label: string, value: React.ReactNode, badge?: boolean }[] }) => (
  <div className="pdf-section mb-10 p-6 md:p-8 rounded-lg bg-surface-2 border border-ink-200 shadow-inner">
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
      {metrics.map((m, i) => (
        <div key={i} className="space-y-2 flex flex-col justify-center">
          <div className="text-[10px] font-medium uppercase tracking-widest text-ink-500">{m.label}</div>
          <div className={cn("text-base md:text-lg font-mono font-medium leading-none", m.badge ? "text-oxblood" : "text-ink")}>
            {m.value}
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const StackedNarrativeCard = ({ title, icon: Icon, value, content }: { title: string, icon: LucideIcon, value?: React.ReactNode, content: React.ReactNode }) => (
  <div className="oi-card p-6 space-y-4">
    <SectionHeader icon={Icon} title={title} className="mb-0" />
    {value && <div className="text-2xl font-serif font-medium italic text-ink">{value}</div>}
    <div className="text-base text-ink-600 leading-relaxed">
      {content}
    </div>
  </div>
);

export const NarrativeGrid = ({ children }: { children: React.ReactNode }) => {
  const childrenArray = React.Children.toArray(children).filter(Boolean);
  const rows = [];
  for (let i = 0; i < childrenArray.length; i += 2) {
    rows.push(childrenArray.slice(i, i + 2));
  }
  return (
    <div className="mb-10">
      {rows.map((row, idx) => (
        <div key={idx} className={cn("pdf-section grid grid-cols-1 md:grid-cols-2 gap-6", idx < rows.length - 1 ? "mb-6" : "")}>
          {row}
        </div>
      ))}
    </div>
  );
};
